import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, Smile, Sparkles, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

/**
 * Geführte Tour beim ersten Login nach der Fusion:
 * erklärt die drei Bereiche (Team / Kunden / Social Faces) und wo was zu finden ist.
 * Wird über user.fusion_tour_seen gesteuert.
 */
const STEPS = [
    {
        icon: Sparkles,
        title: 'Willkommen im neuen AgencyOS!',
        text: 'Aus einem langen Menü wurden drei aufgeräumte Bereiche. Oben links neben dem Logo findest du jetzt ein Dropdown, mit dem du jederzeit zwischen den drei Systemen wechselst.',
        accent: 'bg-primary/10 text-primary',
    },
    {
        icon: LayoutDashboard,
        title: 'Team - dein Arbeitsalltag',
        text: 'Alles Interne bleibt hier: Dashboard, Kalender, Tasks, Zeiten, Projekte, Formulare, Chat, Urlaub, Challenges und der KI-Assistent rechts unten. Genau wie bisher.',
        accent: 'bg-blue-500/10 text-blue-500',
    },
    {
        icon: Users,
        title: 'Kunden - Planung & Freigaben',
        text: 'Der neue Kunden-Bereich vereint die Kundenverwaltung mit dem kompletten Content-Workflow: Redaktionspläne, Post-Freigaben per Kunden-Link, Content-Ideen, Mediathek und Reportings. Die alte Content Bibliothek ist hier aufgegangen.',
        accent: 'bg-emerald-500/10 text-emerald-500',
    },
    {
        icon: Smile,
        title: 'Social Faces - von Bewerbung bis Honorarnote',
        text: 'Social-Faces-Verwaltung, Bewerbungsformulare, Shootings und die monatliche Abrechnung mit automatischer Honorarnote (inkl. SEPA-QR-Code) - alles in einem Bereich. Social Faces melden sich dort selbst per Magic-Link an.',
        accent: 'bg-pink-500/10 text-pink-500',
    },
];

const FusionTour = () => {
    const { user, api, updateUser } = useAuth();
    const [step, setStep] = useState(0);
    const [dismissed, setDismissed] = useState(false);

    const show = user && !user.fusion_tour_seen && !dismissed && user.role !== 'influencer';
    if (!show) return null;

    const finish = async () => {
        setDismissed(true);
        updateUser({ fusion_tour_seen: true });
        try {
            await api.post('/users/me/fusion-tour-seen');
        } catch {
            // nicht kritisch - Tour erscheint schlimmstenfalls noch einmal
        }
    };

    const current = STEPS[step];
    const Icon = current.icon;
    const isLast = step === STEPS.length - 1;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                data-testid="fusion-tour"
            >
                <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-8"
                >
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${current.accent}`}>
                        <Icon className="w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {current.title}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                        {current.text}
                    </p>

                    {/* Fortschritts-Punkte */}
                    <div className="flex items-center gap-1.5 mb-6">
                        {STEPS.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted'}`}
                            />
                        ))}
                    </div>

                    <div className="flex items-center justify-between">
                        <button
                            onClick={finish}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="tour-skip"
                        >
                            Überspringen
                        </button>
                        <div className="flex items-center gap-2">
                            {step > 0 && (
                                <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setStep(step - 1)}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                            )}
                            <Button
                                size="sm"
                                className="rounded-xl gap-1"
                                onClick={() => (isLast ? finish() : setStep(step + 1))}
                                data-testid="tour-next"
                            >
                                {isLast ? "Los geht's" : 'Weiter'}
                                {!isLast && <ChevronRight className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default FusionTour;
